import SockJS from "sockjs-client/dist/sockjs.min.js";
import { Client } from "@stomp/stompjs";
import { API_BASE_URL, SOCKJS_URL } from "../config/api.js";

export default {
  selectAlert({ commit }, alert) {
    commit("selectAlert", alert);
  },
  resetKeepAlive({ commit }) {
    commit("RESET_LAST_KEEP_ALIVE");
  },

  // Auth actions
  async login({ commit }, { username, password }) {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body.success) {
      const isInvalid = response.status === 401 || /invalid credentials/i.test(body?.message || "");
      const code = isInvalid ? "login.invalidCredentials" : "login.failed";
      const message = code; // Let component translate
      const err = new Error(message);
      err.code = code;
      throw err;
    }
    const token = body.token || "dev-token";
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", body.username || username);
    commit("SET_AUTH", { token, username: body.username || username });
  },
  logout({ commit }) {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    commit("CLEAR_AUTH");
  },

  // Load alerts from cache
  async loadAlertsFromCache({ commit, dispatch, state }) {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts`, {
        headers: state.authToken ? { Authorization: `Bearer ${state.authToken}` } : {},
      });
      if (response.ok) {
        const alerts = await response.json();
        commit("SET_ALERTS", alerts);
        // Update statistics after loading alerts
        dispatch("updateStatistics");
      } else {
        console.error("Failed to load alerts from cache:", response.status);
      }
    } catch (error) {
      console.error("Error loading alerts from cache:", error);
    }
  },

  // Start statistics monitoring
  startStatisticsMonitoring({ commit, dispatch }) {
    // Update statistics immediately
    dispatch("updateStatistics");

    // Update statistics every 10 seconds
    const interval = setInterval(() => {
      dispatch("updateStatistics");
    }, 10000);
    commit("setStatisticsInterval", interval);
  },

  stopStatisticsMonitoring({ commit, state }) {
    if (state.statisticsInterval) {
      clearInterval(state.statisticsInterval);
      commit("setStatisticsInterval", null);
    }
  },

  // Advanced Statistics action
  updateStatistics({ commit, state, getters }) {
    const ttlMinutes = getters.uiTtlMinutes || 10;
    const averageMessageRate = state.alerts.length / ttlMinutes;
    commit("setTotalMessages", state.alerts.length);
    commit("setAverageMessageRate", averageMessageRate);
  },

  // WebSocket actions
  connectWebSocket: ({ commit, dispatch }) => {
    const socket = new SockJS(SOCKJS_URL);
    const stompClient = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
    });

    stompClient.onConnect = (frame) => {
      console.log("🔗 WebSocket connected successfully");
      commit("SET_WEBSOCKET_CONNECTED", true);
      commit("SET_CONNECTION_STATUS", "connected");

      // Subscribe to topics
      stompClient.subscribe("/topic/alerts", (message) => {
        const alert = JSON.parse(message.body);
        console.log("🚨 Received new alert:", alert);
        commit("ADD_ALERT", alert);
        dispatch("updateStatistics");
      });
      stompClient.subscribe("/topic/alert-removed", (message) => {
        const removal = JSON.parse(message.body);
        console.log("🗑️ Alert removed:", removal.identifier);
        commit("REMOVE_ALERT", removal.identifier);
        dispatch("updateStatistics");
      });
      stompClient.subscribe("/topic/keep-alive", (message) => {
        const keepAlive = JSON.parse(message.body);
        commit("SET_LAST_KEEP_ALIVE", keepAlive.sentTs);
      });
      stompClient.subscribe("/topic/layer-version", (message) => {
        const layerUpdate = JSON.parse(message.body);
        commit("SET_LAYER_VERSION", layerUpdate);
      });
      stompClient.subscribe("/topic/connection-status", (message) => {
        const status = JSON.parse(message.body);
        commit("SET_CONNECTION_STATUS", status);
      });
      stompClient.subscribe("/topic/history", (message) => {
        const history = JSON.parse(message.body);
        commit("SET_HISTORY_DATA", history);
      });
      stompClient.subscribe("/topic/shapefile-content", (message) => {
        const content = JSON.parse(message.body);
        content.link = content.responseData.link;
        content.version = content.responseData.version;
        console.log("📁 Shapefile content response received");
        commit("SET_SHAPEFILE_CONTENT", content);
      });
      stompClient.subscribe("/topic/shapefile-version", (message) => {
        const version = JSON.parse(message.body);
        console.log("📋 Shapefile version update received");
        commit("SET_SHAPEFILE_VERSION", version);
      });
      stompClient.subscribe("/topic/shapefile-version-response", (message) => {
        const versionResponse = JSON.parse(message.body);
        console.log("📋 Shapefile version response received");
        commit("SET_SHAPEFILE_VERSION_RESPONSE", versionResponse);
      });
      stompClient.subscribe("/topic/history-request", (message) => {
        const historyRequest = JSON.parse(message.body);
        console.log("📚 History request notification received");
        commit("SET_HISTORY_REQUEST", historyRequest);
      });
      stompClient.subscribe("/topic/history-response", (message) => {
        const historyResponse = JSON.parse(message.body);
        historyResponse.link = historyResponse.responseData.link;
        console.log("📚 History response received");
        commit("SET_HISTORY_RESPONSE", historyResponse);
      });
    };

    stompClient.onStompError = (frame) => {
      console.error("STOMP error:", frame);
      commit("SET_WEBSOCKET_CONNECTED", false);
      commit("SET_CONNECTION_STATUS", "error");
    };

    stompClient.onWebSocketClose = () => {
      console.log("❌ WebSocket disconnected");
      commit("SET_WEBSOCKET_CONNECTED", false);
      commit("SET_CONNECTION_STATUS", "disconnected");
    };

    stompClient.activate();
    commit("SET_WEBSOCKET", stompClient);
  },

  // Load last keep-alive from cache on startup
  loadLastKeepAliveFromCache: async ({ commit }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cache/lastKeepAlive`);
      if (response.ok) {
        const text = await response.text();
        if (text) {
          const lastKeepAlive = JSON.parse(text);
          if (lastKeepAlive) {
            commit("SET_LAST_KEEP_ALIVE", lastKeepAlive.sentTs);
          }
        }
      } else {
        console.error("Failed to fetch last KeepAlive:", response.status);
      }
    } catch (error) {
      console.error("Error fetching last KeepAlive:", error);
    }
  },

  disconnectWebSocket: ({ commit, state }) => {
    if (state.websocket) {
      state.websocket.deactivate();
      commit("SET_WEBSOCKET", null);
      commit("SET_WEBSOCKET_CONNECTED", false);
      commit("SET_CONNECTION_STATUS", "disconnected");
    }
  },

  requestHistory: ({ state, commit }) => {
    if (state.websocket && state.websocket.connected) {
      // Generate only numeric request ID
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      const requestId = `${timestamp}${random}`;
      state.websocket.publish({
        destination: "/app/history-request",
        body: JSON.stringify({
          requestId,
          startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Last 24 hours
          endDate: new Date().toISOString(),
        }),
      });
      commit("SET_HISTORY_REQUEST_ID", requestId);
    }
  },

  // New actions for API requirements

  /**
   * Request shapefile content (6.2.1)
   */
  requestShapefileContent: async ({ commit }, requestId) => {
    try {
      console.log("📁 Requesting shapefile content...");
      const response = await fetch(
        `${API_BASE_URL}/capEnablement/shapefile/content`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ requestId }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Shapefile content request sent successfully");
        return result;
      } else {
        console.error("Failed to request shapefile content:", response.status);
      }
    } catch (error) {
      console.error("Error requesting shapefile content:", error);
    }
  },

  /**
   * Update shapefile version (6.3.1)
   */
  updateShapefileVersion: async ({ commit }, { requestId, version }) => {
    try {
      console.log("📋 Updating shapefile version...");
      const response = await fetch(
        `${API_BASE_URL}/capEnablement/shapefile/version`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requestId,
            version,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Shapefile version update sent successfully");
        return result;
      } else {
        console.error("Failed to update shapefile version:", response.status);
      }
    } catch (error) {
      console.error("Error updating shapefile version:", error);
    }
  },

  /**
   * Request history (6.4.1)
   */
  requestHistoryFromAPI: async (
    { commit },
    { requestId, from, to, layerTypes, threatTypes }
  ) => {
    try {
      console.log("📚 Requesting history...");
      const response = await fetch(`${API_BASE_URL}/capEnablement/history`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          requestId,
          from,
          to,
          layerTypes,
          threatTypes,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ History request sent successfully");
        return result;
      } else {
        console.error("Failed to request history:", response.status);
      }
    } catch (error) {
      console.error("Error requesting history:", error);
    }
  },

  async fetchUiTtl({ commit }) {
    try {
      const response = await fetch(`${API_BASE_URL}/cache/ttl`);
      if (response.ok) {
        const minutes = await response.json();
        commit("setUiTtlMinutes", minutes);
      } else {
        console.error("Failed to fetch UI TTL:", response.status);
      }
    } catch (error) {
      console.error("Error fetching UI TTL:", error);
    }
  },
};
